import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Clock3, ExternalLink, MapPin, Phone, X } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { useSiteWorkspace } from '../../context/SiteWorkspaceContext';
import { buildPublicMenuSections, flattenPublicItemsInSectionOrder } from '../../utils/publicMenu';
import ArabAliLogo from '../../components/ArabAliLogo';

const HomePage: React.FC = () => {
  const { restaurant, settings, phoneNumber, hasCallablePhoneNumber, callHref, mapsHref, formatMoney } = useRestaurant();
  const { categories, items } = useSiteWorkspace();
  const [showCallModal, setShowCallModal] = useState(false);

  const menuSections = useMemo(() => buildPublicMenuSections(categories, items), [categories, items]);
  const featuredItems = useMemo(() => flattenPublicItemsInSectionOrder(menuSections).slice(0, 6), [menuSections]);

  const categoryTitleById = useMemo(() => {
    const map = new Map<string, string>();
    menuSections.forEach((section) => map.set(section.id, section.title));
    return map;
  }, [menuSections]);

  return (
    <div className="pb-10">
      <section className="px-4 pb-8 pt-6">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="arab-panel overflow-hidden p-6 sm:p-8"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(246,199,90,0.2),transparent_40%),radial-gradient(circle_at_left,rgba(189,42,32,0.18),transparent_45%)]" />
            <div className="relative">
              <ArabAliLogo
                className="mb-8"
                logoImage={settings.logoImage}
                name={restaurant.name}
                subtitle={settings.subtitle || 'Restaurant'}
                location={restaurant.location}
              />
              <div className="mb-4 inline-flex rounded-full border border-amber-300/20 bg-white/5 px-4 py-1 text-[11px] uppercase tracking-[0.42em] text-amber-100/70">
                {settings.subtitle || 'Menu'}
              </div>
              <h1 className="max-w-3xl font-heading text-4xl font-semibold leading-tight text-white sm:text-6xl">
                Real menu, real pricing, and the identity of <span className="arab-gold-text">{restaurant.name}</span>.
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-amber-50/72 sm:text-lg">{settings.homepageIntro}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/menu" className="arab-button-primary inline-flex items-center gap-2">
                  View Full Menu
                  <ChevronRight className="h-5 w-5" />
                </Link>
                {hasCallablePhoneNumber ? (
                  <button
                    onClick={() => setShowCallModal(true)}
                    className="arab-button-secondary inline-flex items-center gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    {settings.callToActionLabel}
                  </button>
                ) : (
                  <div className="arab-button-secondary inline-flex items-center gap-2 opacity-80">
                    <Phone className="h-4 w-4" />
                    Call Ordering Coming Soon
                  </div>
                )}
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-amber-300/20 hover:bg-white/8"
                >
                  <div className="mb-2 flex items-center gap-2 text-amber-300">
                    <MapPin className="h-4 w-4" />
                    <ExternalLink className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-white/45">Location</div>
                  <div className="mt-1 text-sm font-medium text-white">{restaurant.location}</div>
                </a>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-2 flex items-center gap-2 text-amber-300">
                    <Clock3 className="h-4 w-4" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-white/45">Service</div>
                  <div className="mt-1 text-sm font-medium text-white">Daily 7AM - 11PM</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-2 flex items-center gap-2 text-amber-300">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-white/45">Contact</div>
                  <div className="mt-1 text-sm font-medium text-white">{phoneNumber}</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="arab-panel overflow-hidden p-3"
          >
            <div className="relative h-full min-h-[360px] overflow-hidden rounded-[28px] border border-amber-300/15">
              <img
                src={restaurant.coverImage}
                alt={`${restaurant.name} menu cover`}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#110606] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur-md">
                  <div className="text-[11px] uppercase tracking-[0.4em] text-amber-100/60">Cover</div>
                  <div className="mt-2 arab-gold-text text-2xl font-semibold">{restaurant.name}</div>
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-white/65 transition hover:text-white"
                  >
                    <span>{restaurant.location}</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-4 pb-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/55">Sections</div>
              <h2 className="mt-2 font-heading text-3xl font-semibold text-white">Browse by category</h2>
            </div>
            <Link to="/menu" className="text-sm uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200">
              See all
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {menuSections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <Link
                  to={`/menu?section=${section.id}`}
                  className="group block overflow-hidden rounded-[28px] border border-amber-300/15 bg-[#20100c] shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={section.coverImage}
                      alt={section.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#120705] via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="arab-gold-text text-2xl font-semibold">{section.title}</div>
                      <div className="mt-1 text-sm text-white/70">{section.items.length} items</div>
                    </div>
                  </div>
                  <div className="p-4 text-sm leading-6 text-amber-50/70">{section.shortDescription}</div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-5 text-[11px] uppercase tracking-[0.35em] text-amber-100/55">Featured picks</div>
          <div className="grid gap-4 lg:grid-cols-2">
            {featuredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -14 : 14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.04 * index }}
              >
                <Link to={`/item/${item.id}`} className="arab-list-row group block">
                  <div>
                    <div className="text-lg font-semibold text-white transition-colors group-hover:text-amber-200">{item.name}</div>
                    <div className="mt-1 text-sm text-white/50">{categoryTitleById.get(item.categoryId) ?? 'Menu'}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="arab-price-pill">{formatMoney(item.price)}</div>
                    <ChevronRight className="h-4 w-4 text-amber-300/60 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {showCallModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={() => setShowCallModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="arab-panel relative max-w-sm p-6 text-center"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                onClick={() => setShowCallModal(false)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="mb-4 text-6xl">📞</div>
              <h3 className="mb-2 text-2xl font-semibold text-white">Call {restaurant.name}</h3>
              <p className="mb-6 text-white/60">
                {hasCallablePhoneNumber
                  ? 'Use the number below to reach the restaurant.'
                  : 'The hotel phone number will appear here once it is added.'}
              </p>

              <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="mb-1 text-sm text-white/50">Contact</p>
                <p className="arab-gold-text text-3xl font-semibold">{phoneNumber}</p>
              </div>

              {callHref ? (
                <a href={callHref} className="arab-button-primary inline-flex w-full items-center justify-center gap-2">
                  <Phone className="h-5 w-5" />
                  <span>{settings.callToActionLabel}</span>
                </a>
              ) : (
                <div className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white/70">
                  The hotel phone number will appear here once it is available.
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
